import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { CampaignAccessApiError } from "./campaignAccessApi";

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const DRAFT_SCHEMA_VERSION = "campaign_uat_questionnaire_draft_mutation.v1";
const CLEAR_CONFIRMATION = "clear_testerson_2_saved_draft";
const RESPONSE_SUBMIT_SCHEMA_VERSION =
  "campaign_uat_questionnaire_response_submit.v1";
const RESPONSE_SUBMIT_CONFIRMATION =
  "SUBMIT_TESTERSON_2_RESPONSE_REHEARSAL";
const GENERIC_DRAFT_ERROR =
  "The synthetic saved draft is temporarily unavailable.";
const GENERIC_RESPONSE_ERROR =
  "The synthetic response could not be submitted.";

const utf8ByteLength = (value) => {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0x7f) bytes += 1;
    else if (codePoint <= 0x7ff) bytes += 2;
    else if (codePoint <= 0xffff) bytes += 3;
    else bytes += 4;
  }
  return bytes;
};

const containsUnpairedSurrogate = (value) => {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return true;
      index += 1;
    } else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
      return true;
    }
  }
  return false;
};

const containsDisallowedDraftText = (value) => {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (
      (codePoint >= 0 && codePoint <= 0x1f)
      || (codePoint >= 0x7f && codePoint <= 0x9f)
      || codePoint === 0x061c
      || codePoint === 0x200e
      || codePoint === 0x200f
      || codePoint === 0x2028
      || codePoint === 0x2029
      || (codePoint >= 0x202a && codePoint <= 0x202e)
      || (codePoint >= 0x2066 && codePoint <= 0x2069)
    ) return true;
  }
  return false;
};

const truncateToBudget = (rawValue, maximumScalars, maximumBytes) => {
  if (containsUnpairedSurrogate(rawValue)) return rawValue;
  const normalized = rawValue.normalize("NFC");
  const characters = Array.from(normalized).slice(0, maximumScalars);
  while (characters.length > 0 && utf8ByteLength(characters.join("")) > maximumBytes) {
    characters.pop();
  }
  return characters.join("");
};

const validationMessage = (value, maximumScalars, maximumBytes) => {
  if (value.length === 0) return "Enter a synthetic value before saving.";
  if (containsUnpairedSurrogate(value)) return "This value contains an invalid character.";
  if (value.normalize("NFC") !== value) return "This value is not in the required text form.";
  if (value !== value.trim()) return "Remove leading or trailing spaces before saving.";
  if (containsDisallowedDraftText(value)) return "Use a single line without control characters.";
  if (Array.from(value).length > maximumScalars) {
    return `Use no more than ${maximumScalars} characters.`;
  }
  if (utf8ByteLength(value) > maximumBytes) {
    return `This value exceeds the ${maximumBytes}-byte limit.`;
  }
  return "";
};

const exactPersistedPreview = (preview) => {
  const question = preview?.questionnaire?.sections?.[0]?.questions?.[0];
  const sections = preview?.questionnaire?.sections;
  const questions = sections?.[0]?.questions;
  const capabilities = preview?.capabilities;
  const persistence = preview?.draftPersistence;
  const response = preview?.responseRehearsal;
  const safety = preview?.safety;
  const responsePreview = preview?.schemaVersion
    === "campaign_uat_public_questionnaire_preview.v4";
  return Boolean(
    [
      "campaign_uat_public_questionnaire_preview.v3",
      "campaign_uat_public_questionnaire_preview.v4",
    ].includes(preview?.schemaVersion)
    && preview?.source === "immutable_blueprint_run_snapshot"
    && Array.isArray(sections)
    && sections.length === 1
    && sections[0]?.sequence === 1
    && Array.isArray(questions)
    && questions.length === 1
    && /^cq_v1_[A-Za-z0-9_-]{43}$/.test(String(question?.questionRef || ""))
    && question.sequence === 1
    && question.responseType === "text"
    && question.required === false
    && question.conditionalRequired === false
    && question.conditionalVisibility === false
    && Array.isArray(question.options)
    && question.options.length === 0
    && capabilities?.draftReadAllowed === true
    && capabilities?.draftAnswerEntryAllowed === true
    && capabilities?.draftSaveAllowed === true
    && capabilities?.draftClearAllowed === true
    && capabilities?.fileUploadAllowed === false
    && capabilities?.submitAllowed === false
    && capabilities?.bookingAllowed === false
    && capabilities?.crmWriteAllowed === false
    && persistence?.schemaVersion
      === "campaign_uat_public_questionnaire_draft_persistence.v1"
    && persistence.mode === "same_verified_session_encrypted_text"
    && persistence.allowed === true
    && persistence.sessionBound === true
    && Array.isArray(persistence.supportedResponseTypes)
    && persistence.supportedResponseTypes.length === 1
    && persistence.supportedResponseTypes[0] === "text"
    && persistence.maxAnswers === 1
    && persistence.maxCharacters === 200
    && persistence.maxUtf8Bytes === 800
    && persistence.clearAllowed === true
    && persistence.browserStorageAllowed === false
    && persistence.telemetryAllowed === false
    && persistence.submissionAllowed === false
    && (!responsePreview || (
      response?.schemaVersion
        === "campaign_uat_public_questionnaire_response_rehearsal.v1"
      && response.mode === "single_testerson_text_response"
      && response.allowed === true
      && response.explicitConfirmationRequired === true
      && response.rehearsalSubmitAllowed === true
      && response.legacySubmissionWriteAllowed === false
      && response.engagementWriteAllowed === false
    ))
    && safety?.providerMode === "disabled"
    && safety.providerCallsAllowed === false
    && safety.emailSent === false
    && safety.executionAllowed === false
    && safety.claimable === false
    && safety.queueState === "not_queued"
    && safety.deliveryStatus === "not_sent"
  );
};

const initialSavedValue = (current, questionRef) => (
  current?.state === "saved"
  && current?.answers?.length === 1
  && current.answers[0]?.questionRef === questionRef
    ? current.answers[0].value
    : ""
);

const canonicalUuidV4 = () => {
  if (typeof window.crypto?.randomUUID !== "function") return null;
  const value = String(window.crypto.randomUUID()).toLowerCase();
  return UUID_V4_PATTERN.test(value) ? value : null;
};

const clearInputNode = (node) => {
  if (!node) return;
  node.value = "";
  node.defaultValue = "";
  node.removeAttribute("value");
};

export default function CampaignPersistedQuestionnaireDraft({
  preview,
  current,
  expiresAt,
  onMutate,
  onApplied,
  onSubmit,
  onSubmitted,
  onReload,
  onTerminal,
}) {
  const exactPreview = exactPersistedPreview(preview);
  const question = exactPreview
    ? preview.questionnaire.sections[0].questions[0]
    : null;
  const savedValue = initialSavedValue(current, question?.questionRef);
  const [dirty, setDirty] = useState(false);
  const [issue, setIssue] = useState("");
  const [publicError, setPublicError] = useState("");
  const [phase, setPhase] = useState("idle");
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  const inputRef = useRef(null);
  const reviewValueRef = useRef(null);
  const valueRef = useRef(savedValue);
  const pendingRef = useRef(null);
  const responsePendingRef = useRef(null);
  const actionGuardRef = useRef(false);
  const mountedRef = useRef(false);

  const captureInput = useCallback((node) => {
    const previous = inputRef.current;
    if (previous && previous !== node) clearInputNode(previous);
    inputRef.current = node;
    if (node) {
      node.value = valueRef.current;
      node.defaultValue = "";
      node.removeAttribute("value");
    }
  }, []);

  const maximumScalars = preview?.draftPersistence?.maxCharacters ?? 0;
  const maximumBytes = preview?.draftPersistence?.maxUtf8Bytes ?? 0;

  const purge = useCallback(() => {
    valueRef.current = "";
    pendingRef.current = null;
    responsePendingRef.current = null;
    actionGuardRef.current = false;
    clearInputNode(inputRef.current);
    if (reviewValueRef.current) reviewValueRef.current.textContent = "";
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const onPageHide = () => purge();
    const onPageShow = () => purge();
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      purge();
    };
  }, [purge]);

  useLayoutEffect(() => {
    valueRef.current = savedValue;
    if (inputRef.current) inputRef.current.value = savedValue;
    pendingRef.current = null;
    actionGuardRef.current = false;
    setDirty(false);
    setIssue("");
    setPublicError("");
    setPhase("idle");
    setConfirmingClear(false);
    setConfirmingSubmit(false);
  }, [current?.revision, current?.state, savedValue]);

  const characterCount = Array.from(valueRef.current).length;

  if (!exactPreview || !current || current.schemaVersion
    !== "campaign_uat_questionnaire_draft_state.v1") return null;

  const resultMatchesMutation = (result, pending) => Boolean(
    result
    && result.schemaVersion === "campaign_uat_questionnaire_draft_mutation_result.v1"
    && result.state === (pending.mutation.action === "save" ? "saved" : "cleared")
    && result.revision === pending.mutation.expectedRevision + 1
    && result.expiresAt === expiresAt
    && typeof result.changedAt === "string"
    && Number.isFinite(new Date(result.changedAt).getTime())
    && new Date(result.changedAt).getTime() < new Date(expiresAt).getTime()
    && typeof result.idempotentReplay === "boolean"
  );

  const runPendingMutation = async (pending, retrying = false) => {
    if (actionGuardRef.current || pendingRef.current !== pending) return;
    actionGuardRef.current = true;
    setConfirmingClear(false);
    setPublicError("");
    setPhase(pending.mutation.action === "save" ? "saving" : "clearing");
    try {
      const result = await onMutate(pending.mutation);
      if (!mountedRef.current || pendingRef.current !== pending) return;
      if (!resultMatchesMutation(result, pending)) {
        purge();
        onTerminal(new CampaignAccessApiError());
        return;
      }
      pendingRef.current = null;
      if (pending.mutation.action === "clear") {
        // Clear the sensitive value synchronously at the successful server
        // boundary. Do not wait for the parent revision update/effect: a page
        // lifecycle transition could occur before React commits that render.
        valueRef.current = "";
        clearInputNode(inputRef.current);
      }
      onApplied({
        result,
        savedValue: pending.mutation.action === "save" ? pending.savedValue : "",
        questionRef: question.questionRef,
      });
    } catch (error) {
      if (!mountedRef.current || pendingRef.current !== pending) return;
      const apiError = error instanceof CampaignAccessApiError ? error : null;
      if (apiError?.transportFailure && !retrying) {
        setPublicError(
          "The save result is unknown. Retry the exact same request once.",
        );
        setPhase(pending.mutation.action === "save" ? "retry_save" : "retry_clear");
      } else if (apiError?.draftConflict) {
        pendingRef.current = null;
        setPublicError(apiError.publicMessage || GENERIC_DRAFT_ERROR);
        setPhase("conflict");
      } else if (apiError?.draftValidation) {
        pendingRef.current = null;
        setPublicError(apiError.publicMessage || GENERIC_DRAFT_ERROR);
        setPhase("idle");
      } else {
        purge();
        onTerminal(apiError || new CampaignAccessApiError());
      }
    } finally {
      actionGuardRef.current = false;
    }
  };

  const startSave = () => {
    if (phase !== "idle" || !dirty || issue || pendingRef.current) return;
    const value = valueRef.current;
    const currentIssue = validationMessage(value, maximumScalars, maximumBytes);
    if (currentIssue) {
      setIssue(currentIssue);
      return;
    }
    const idempotencyKey = canonicalUuidV4();
    if (!idempotencyKey) {
      setPublicError(GENERIC_DRAFT_ERROR);
      return;
    }
    const mutation = Object.freeze({
      schemaVersion: DRAFT_SCHEMA_VERSION,
      action: "save",
      idempotencyKey,
      expectedRevision: current.revision,
      answers: Object.freeze([
        Object.freeze({ questionRef: question.questionRef, value }),
      ]),
    });
    const pending = { mutation, savedValue: value };
    pendingRef.current = pending;
    runPendingMutation(pending);
  };

  const startClear = () => {
    if (phase !== "idle" || current.state !== "saved" || pendingRef.current) return;
    const idempotencyKey = canonicalUuidV4();
    if (!idempotencyKey) {
      setPublicError(GENERIC_DRAFT_ERROR);
      return;
    }
    const mutation = Object.freeze({
      schemaVersion: DRAFT_SCHEMA_VERSION,
      action: "clear",
      idempotencyKey,
      expectedRevision: current.revision,
      confirmation: CLEAR_CONFIRMATION,
    });
    const pending = { mutation, savedValue: "" };
    pendingRef.current = pending;
    runPendingMutation(pending);
  };

  const resetUnsaved = () => {
    if (phase !== "idle" || !dirty) return;
    valueRef.current = savedValue;
    if (inputRef.current) inputRef.current.value = savedValue;
    setDirty(false);
    setIssue("");
    setPublicError("");
    setConfirmingClear(false);
    setConfirmingSubmit(false);
  };

  const retryPending = () => {
    const pending = pendingRef.current;
    if (!pending || !["retry_save", "retry_clear"].includes(phase)) return;
    runPendingMutation(pending, true);
  };

  const responseResultMatches = (result) => Boolean(
    result
    && result.schemaVersion === "campaign_uat_questionnaire_response_receipt.v1"
    && result.state === "submitted"
    && result.responseRevision === 1
    && result.answerCount === 1
    && typeof result.submittedAt === "string"
    && Number.isFinite(new Date(result.submittedAt).getTime())
    && result.reviewedAt === null
    && typeof result.idempotentReplay === "boolean"
  );

  const runPendingResponseSubmit = async (pending, retrying = false) => {
    if (actionGuardRef.current || responsePendingRef.current !== pending) return;
    actionGuardRef.current = true;
    setPublicError("");
    setPhase("submitting");
    try {
      const result = await onSubmit(pending.mutation);
      if (!mountedRef.current || responsePendingRef.current !== pending) return;
      if (!responseResultMatches(result)) {
        purge();
        onTerminal(new CampaignAccessApiError());
        return;
      }
      // The answer must leave the live DOM and all mutable refs at the exact
      // successful submission boundary, before the receipt render commits.
      purge();
      onSubmitted(result);
    } catch (error) {
      if (!mountedRef.current || responsePendingRef.current !== pending) return;
      const apiError = error instanceof CampaignAccessApiError ? error : null;
      if (apiError?.transportFailure && !retrying) {
        setPublicError(
          "The submit result is unknown. Retry the exact same request once.",
        );
        setPhase("retry_submit");
      } else if (apiError?.responseConflict) {
        // A competing tab or an ambiguous earlier request may already have
        // committed. Clear local content first, then restore the server's
        // answer-free lifecycle projection through the session cookie.
        purge();
        onReload();
      } else if (apiError?.responseValidation) {
        responsePendingRef.current = null;
        setConfirmingSubmit(false);
        setPublicError(apiError.publicMessage || GENERIC_RESPONSE_ERROR);
        setPhase("idle");
      } else {
        purge();
        onTerminal(apiError || new CampaignAccessApiError());
      }
    } finally {
      actionGuardRef.current = false;
    }
  };

  const startResponseReview = () => {
    if (
      phase !== "idle"
      || dirty
      || current.state !== "saved"
      || !savedValue
      || responsePendingRef.current
    ) return;
    setPublicError("");
    setConfirmingClear(false);
    setConfirmingSubmit(true);
  };

  const confirmResponseSubmit = () => {
    if (
      phase !== "idle"
      || !confirmingSubmit
      || dirty
      || current.state !== "saved"
      || current.revision < 1
      || !savedValue
      || responsePendingRef.current
    ) return;
    const idempotencyKey = canonicalUuidV4();
    if (!idempotencyKey) {
      setPublicError(GENERIC_RESPONSE_ERROR);
      return;
    }
    const mutation = Object.freeze({
      schemaVersion: RESPONSE_SUBMIT_SCHEMA_VERSION,
      action: "submit",
      confirmation: RESPONSE_SUBMIT_CONFIRMATION,
      expectedDraftRevision: current.revision,
      idempotencyKey,
    });
    const pending = { mutation };
    responsePendingRef.current = pending;
    runPendingResponseSubmit(pending);
  };

  const retryResponseSubmit = () => {
    const pending = responsePendingRef.current;
    if (!pending || phase !== "retry_submit") return;
    runPendingResponseSubmit(pending, true);
  };

  const processing = [
    "saving",
    "clearing",
    "submitting",
    "retry_save",
    "retry_clear",
    "retry_submit",
    "conflict",
  ]
    .includes(phase);
  const responseEnabled = preview.schemaVersion
    === "campaign_uat_public_questionnaire_preview.v4";
  const statusLabel = current.state === "saved"
    ? `Saved draft · revision ${current.revision}`
    : current.state === "cleared"
      ? `Cleared draft · revision ${current.revision}`
      : "No saved draft · revision 0";

  return (
    <section
      aria-labelledby="campaign-persisted-draft-title"
      className="mt-5 rounded-xl border border-cyan-200 bg-cyan-50 p-5"
      data-testid="campaign-persisted-questionnaire-draft"
    >
      <h2 id="campaign-persisted-draft-title" className="text-lg font-semibold text-cyan-950">
        Synthetic saved-draft rehearsal
      </h2>
      <p className="mt-2 text-sm text-cyan-950">
        This one Testerson 2 value is encrypted and saved only to this active
        verified session. {responseEnabled
          ? "A separate explicit confirmation can submit it to this isolated rehearsal."
          : "It is not a questionnaire submission and is not shared with a new OTP session."}
      </p>
      <p className="mt-1 text-xs text-cyan-900">
        There is no autosave, browser storage, upload, booking, CRM write, email,
        queue work, or provider call.
      </p>

      <div className="mt-4 rounded-lg border border-cyan-200 bg-white p-4">
        <p className="text-sm font-medium text-slate-900" role="status">
          {statusLabel}
        </p>
        {current.changedAt ? (
          <p className="mt-1 text-xs text-slate-600">
            Server changed this draft at {new Date(current.changedAt).toLocaleString()}.
          </p>
        ) : null}

        <label className="mt-4 block text-sm font-medium text-slate-900" htmlFor="campaign-persisted-draft-value">
          {question.label}
        </label>
        <input
          aria-describedby="campaign-persisted-draft-notice campaign-persisted-draft-validation"
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 disabled:bg-slate-100"
          disabled={processing || confirmingSubmit}
          id="campaign-persisted-draft-value"
          onChange={(event) => {
            const value = truncateToBudget(
              event.currentTarget.value,
              maximumScalars,
              maximumBytes,
            );
            if (event.currentTarget.value !== value) event.currentTarget.value = value;
            valueRef.current = value;
            setDirty(value !== savedValue);
            setIssue(validationMessage(value, maximumScalars, maximumBytes));
            setPublicError("");
            setConfirmingClear(false);
            setConfirmingSubmit(false);
          }}
          onCopy={(event) => event.preventDefault()}
          onCut={(event) => event.preventDefault()}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.preventDefault();
          }}
          onPaste={(event) => event.preventDefault()}
          ref={captureInput}
          spellCheck={false}
          type="text"
        />
        <div className="mt-1 flex flex-wrap justify-between gap-2 text-xs text-slate-600">
          <p id="campaign-persisted-draft-notice">
            Synthetic value only. Save is explicit and session-bound.
          </p>
          <p>{characterCount} / {maximumScalars}</p>
        </div>
        <p className="mt-1 text-xs text-amber-800" id="campaign-persisted-draft-validation">
          {dirty && issue ? issue : ""}
        </p>

        {publicError ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
            {publicError}
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          {phase === "idle" ? (
            <>
              <button
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!dirty || Boolean(issue)}
                onClick={startSave}
                type="button"
              >
                Save draft
              </button>
              <button
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!dirty}
                onClick={resetUnsaved}
                type="button"
              >
                Reset unsaved
              </button>
              {responseEnabled
                && current.state === "saved"
                && !confirmingClear
                && !confirmingSubmit ? (
                <button
                  className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={dirty}
                  onClick={startResponseReview}
                  type="button"
                >
                  Review synthetic response
                </button>
              ) : null}
              {current.state === "saved" && !confirmingClear && !confirmingSubmit ? (
                <button
                  className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700"
                  onClick={() => setConfirmingClear(true)}
                  type="button"
                >
                  Clear saved draft
                </button>
              ) : null}
            </>
          ) : null}
          {phase === "saving" ? <p role="status">Saving synthetic draft…</p> : null}
          {phase === "clearing" ? <p role="status">Clearing saved draft…</p> : null}
          {phase === "submitting" ? <p role="status">Submitting synthetic response…</p> : null}
          {["retry_save", "retry_clear"].includes(phase) ? (
            <button
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white"
              onClick={retryPending}
              type="button"
            >
              Retry {phase === "retry_save" ? "save" : "clear"} draft
            </button>
          ) : null}
          {phase === "retry_submit" ? (
            <button
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white"
              onClick={retryResponseSubmit}
              type="button"
            >
              Retry exact response submission
            </button>
          ) : null}
          {phase === "conflict" ? (
            <button
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white"
              onClick={onReload}
              type="button"
            >
              Reload saved draft
            </button>
          ) : null}
        </div>

        {confirmingClear && phase === "idle" ? (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-900">
              Clear the active saved Testerson 2 value? The revision evidence remains,
              but this is not a claim of erasure from backups.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white"
                onClick={startClear}
                type="button"
              >
                Confirm clear saved draft
              </button>
              <button
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800"
                onClick={() => setConfirmingClear(false)}
                type="button"
              >
                Cancel clear
              </button>
            </div>
          </div>
        ) : null}

        {confirmingSubmit && phase === "idle" ? (
          <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50 p-3">
            <h3 className="text-sm font-semibold text-violet-950">
              Review before synthetic submission
            </h3>
            <p className="mt-2 text-xs text-violet-900">
              This submits only to the isolated Testerson 2 response rehearsal.
              It does not invoke providers, email, a legacy questionnaire
              submission, engagement activity, booking, or CRM changes.
            </p>
            <dl className="mt-3 rounded-lg border border-violet-200 bg-white p-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {question.label}
              </dt>
              <dd
                className="mt-1 break-words text-sm text-slate-900"
                onCopy={(event) => event.preventDefault()}
                ref={reviewValueRef}
              >
                {savedValue}
              </dd>
            </dl>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white"
                onClick={confirmResponseSubmit}
                type="button"
              >
                Confirm synthetic submission
              </button>
              <button
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800"
                onClick={() => setConfirmingSubmit(false)}
                type="button"
              >
                Cancel submission
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
